import React from 'react';
import { Button, Content } from '@leverege/ui-elements';
import { TitleBar } from '@leverege/ui-plugin';
import I18N from '@leverege/i18n';
import eq from 'fast-deep-equal';
import AttributeFormFactory from "./AttributeFormFactory.js";
import AttributeObjectValidator from "./validator/AttributeObjectValidator.js";
import SetterUtil from "./SetterUtil.js";
import DataSources from "../../dataSource/DataSources.js";
import Attributes from "../Attributes.js";
import ErrorStyle from './AttributeGridError.css';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const {
  t
} = I18N.ns('ui-attributes.attribute.editor.AttributeGridEditor');
export default class AttributeGridEditor2 extends React.Component {
  static defaultProps = {
    get okayLabel() {
      return t('okay');
    },
    get cancelLabel() {
      return t('cancel');
    }
  };
  constructor(props) {
    super(props);
    this.formContainerRef = /*#__PURE__*/React.createRef();
    this.transaction = null;
    const {
      target,
      initialValues,
      attributes,
      context
    } = props;
    const objRef = {
      ...target,
      data: target.data || DataSources.getData(target)
    };
    this.editableAttributes = attributes.reduce((prev, attr) => {
      let a;
      if (typeof attr.get === 'function') {
        a = attr;
      } else {
        a = Attributes.getAttribute(attr.name, target?.type);
      }
      if (a.canSet && a.canSet(objRef, undefined, context)) {
        prev.push(a);
      }
      return prev;
    }, []);
    let value = initialValues ? {
      ...initialValues
    } : {};
    if (target != null) {
      value = {
        ...value,
        ...SetterUtil.toValues(this.editableAttributes, objRef, context)
      };
    }
    if (target?.id == null) {
      // its a create, so things in initialValues are also changes
      this.transaction = SetterUtil.toTransaction(target, value, {}, context, this.transaction);
    }
    this.state = {
      value,
      original: value,
      active: {},
      errors: {},
      changeKey: Date.now(),
      applying: false,
      validating: false
    };
    this.validator = new AttributeObjectValidator({
      attributes: this.editableAttributes,
      object: target,
      context
    });
  }
  static getDerivedStateFromProps(props, state) {
    if (state.value === state.original) {
      return {
        hasChanged: false
      };
    }
    return {
      hasChanged: !eq(state.value, state.original)
    };
  }
  validate(evt, allFields = false) {
    const loadingTimer = setTimeout(() => {
      this.setState({
        validating: true
      });
    }, 250);
    const pValidate = this.validator.validate(evt, allFields);
    pValidate.then(res => {
      clearTimeout(loadingTimer);
      this.setState({
        validating: false
      });
      return res;
    });
    return pValidate;
  }
  onChange = async evt => {
    const {
      value,
      active
    } = this.state;
    const {
      target,
      context
    } = this.props;
    const {
      value: nValue,
      changes
    } = SetterUtil.createDataChange(evt, value);
    const {
      error,
      result
    } = await this.validate({
      data: evt.data,
      value: nValue
    });
    const flatError = error?.flatten();
    if (!flatError?.fieldErrors?.[evt.data]) {
      this.transaction = SetterUtil.toTransaction(target, changes, active, context, this.transaction);
    }
    this.setState({
      value: result,
      errors: flatError
    });
  };
  onOkay = async () => {
    const {
      value,
      active
    } = this.state;
    const {
      onOkay,
      onSuccess,
      onError
    } = this.props;
    const {
      error
    } = await this.validate({
      value
    }, true);
    if (error == null) {
      if (onOkay && onOkay(value, active) === false) {
        return; // external handling
      }
      if (this.transaction) {
        this.setState({
          applying: true
        }, () => {
          this.transaction.run().then(r => {
            this.setState({
              applying: false,
              submitError: null,
              original: value
            });
            if (onSuccess) {
              onSuccess(r);
            }
            this.transaction = null;
          }, error => {
            this.setState({
              applying: false,
              submitError: error
            });
            if (onError) {
              onError(error);
            }
          });
        });
      }
    } else {
      this.setState({
        errors: error?.flatten()
      }, () => {
        this.formContainerRef.current.querySelector(`.${ErrorStyle.errorContainer}`)?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
        this.highlightErrors();
      });
    }
  };
  highlightErrors(ms = 1000) {
    setTimeout(() => {
      this.setState({
        highlightErrors: true
      });
      setTimeout(() => {
        this.setState({
          highlightErrors: false
        });
      }, 2000);
    }, ms);
  }
  onCancel = () => {
    const {
      onCancel
    } = this.props;
    const {
      original
    } = this.state;
    this.setState({
      value: original,
      errors: {},
      changeKey: Date.now(),
      hasChanged: false
    });
    if (onCancel) {
      onCancel();
    }
  };
  render() {
    const {
      areaClassName,
      cancelLabel,
      className,
      context,
      footerClassName,
      headerClassName,
      icon,
      okayLabel,
      target,
      title,
      onCancel
    } = this.props;
    const {
      active,
      applying,
      changeKey,
      errors,
      hasChanged,
      highlightErrors,
      validating,
      value
    } = this.state;
    const editor = AttributeFormFactory.create(target?.type, {
      active,
      applying,
      attributes: this.editableAttributes,
      context,
      errors,
      highlightErrors,
      object: target,
      onChange: this.onChange,
      onCancel,
      target,
      validating,
      value,
      validator: this.validator
    });
    return /*#__PURE__*/_jsxs(Content, {
      className: className,
      variant: "itemForm",
      children: [/*#__PURE__*/_jsx(Content.Header, {
        className: headerClassName,
        variant: "itemFormHeader",
        children: /*#__PURE__*/_jsx(TitleBar, {
          variant: "dialogTitle",
          title: title,
          icon: icon
        })
      }), /*#__PURE__*/_jsx(Content.Area, {
        className: areaClassName,
        variant: "formBody",
        domRef: this.formContainerRef,
        children: editor
      }), /*#__PURE__*/_jsxs(Content.Footer, {
        className: footerClassName,
        variant: "formButtons",
        layout: "flex:rowMEnd",
        children: [/*#__PURE__*/_jsx(Button, {
          variant: "secondary",
          onClick: this.onCancel,
          children: cancelLabel
        }), /*#__PURE__*/_jsx(Button, {
          variant: "primary",
          disabled: !hasChanged,
          onClick: this.onOkay,
          children: okayLabel
        })]
      })]
    }, changeKey);
  }
}